import React, { useState } from 'react';
import { Button } from '@rsos/base-components';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import useLocationPicker from '../LocationDebugPanel/useLocationPicker';
import {
  sendLocation,
  simulateTcapiCall,
  simulateTcapiMessage,
} from '../services/els';
import {
  StyledDescription,
  StyledError,
  StyledHint,
  StyledLocationInfo,
  StyledSuccess,
} from './TextTo911DebugPanel.styles';

type Status = 'idle' | 'sending' | 'sent' | 'error';

interface SimulateCallControlsProps {
  phoneNumber: string;
  callId: string | null;
}

/**
 * Controls for simulating a text-to-911 session. Posts a location to ELS and
 * triggers Shanghai's mock TCAPI server to emit a callPresented event. When a
 * TCAPI call is selected in the queue, shows a message input that sends
 * simulated inbound messages from the caller via Shanghai's simulate-message
 * endpoint.
 */
export function SimulateCallControls({
  phoneNumber,
  callId,
}: SimulateCallControlsProps) {
  const map = window.__RSOS?.map;
  const location = useLocationPicker(map);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [messageText, setMessageText] = useState('');
  const [messageStatus, setMessageStatus] = useState<Status>('idle');
  const [messageError, setMessageError] = useState('');

  async function handleSimulate() {
    if (!location) return;

    setStatus('sending');
    setErrorMessage('');

    try {
      await sendLocation({
        latitude: location.latitude,
        longitude: location.longitude,
        phoneNumber,
        source: 'tcapi',
      });

      await simulateTcapiCall(phoneNumber);
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Unknown error occurred',
      );
    }
  }

  async function handleSendMessage() {
    if (!callId || !messageText.trim()) return;

    setMessageStatus('sending');
    setMessageError('');

    try {
      await simulateTcapiMessage(callId, messageText.trim());
      setMessageStatus('sent');
      setMessageText('');
      setTimeout(() => setMessageStatus('idle'), 2000);
    } catch (err) {
      setMessageStatus('error');
      setMessageError(
        err instanceof Error ? err.message : 'Failed to send message',
      );
    }
  }

  if (!location) {
    return (
      <StyledHint>
        <strong>Click the map</strong> to select a location inside the
        jurisdiction.
      </StyledHint>
    );
  }

  return (
    <div>
      <StyledLocationInfo>
        <div>
          <strong>Latitude:</strong> {location.latitude.toFixed(6)}
        </div>
        <div>
          <strong>Longitude:</strong> {location.longitude.toFixed(6)}
        </div>
      </StyledLocationInfo>
      <Button
        onClick={handleSimulate}
        btnType="primary"
        disabled={status === 'sending'}
      >
        <strong>
          {status === 'sending' ? 'Sending...' : 'Simulate Text-to-911 Session'}
        </strong>
      </Button>
      {status === 'sent' && (
        <StyledSuccess>
          Text-to-911 session simulated via Shanghai mock TCAPI. The call should
          appear in the queue shortly.
        </StyledSuccess>
      )}
      {status === 'error' && <StyledError>Error: {errorMessage}</StyledError>}
      {callId && (
        <div style={{ marginTop: '12px' }}>
          <StyledDescription>
            Simulate an inbound message from the caller:
          </StyledDescription>
          <div
            style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter' && messageText.trim()) {
                handleSendMessage();
              }
            }}
          >
            <div style={{ flex: 1 }}>
              <InputText
                type="text"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setMessageText(event.target.value)
                }
                value={messageText}
                placeholder="Type a message from the caller..."
              />
            </div>
            <Button
              onClick={handleSendMessage}
              btnType="secondary"
              disabled={messageStatus === 'sending' || !messageText.trim()}
            >
              <strong>
                {messageStatus === 'sending' ? 'Sending...' : 'Send'}
              </strong>
            </Button>
          </div>
          {messageStatus === 'sent' && (
            <StyledSuccess>Message sent.</StyledSuccess>
          )}
          {messageStatus === 'error' && (
            <StyledError>Error: {messageError}</StyledError>
          )}
        </div>
      )}
    </div>
  );
}
